import { tableAnswerData, tableDisplayRows, tableHasRowLabels } from "./tableQuestion";

/*
  Print view of a complete form answer: opens a clean page (no admin chrome)
  and the browser's print dialog, where "Save as PDF" is one of the printers.

  Everything shown comes from the respondent, so every value is escaped.
*/

function escapeHtml(value) {
    return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function multiline(value) {
    return escapeHtml(value).replace(/\r?\n/g, "<br>");
}

const IMAGE_EXTENSION = /\.(jpe?g|png|gif|webp|bmp|avif)(\?|#|$)/i;

function safeUrl(url) {
    return /^https?:\/\//i.test(String(url || "")) ? String(url) : null;
}

function isAnswered(answer) {
    if (!answer || !answer.id) return false;
    if (answer.type === "file") return (answer.files || []).length > 0;
    if (answer.type === "table") return !!tableAnswerData(answer);
    if (answer.valueNumber !== null && answer.valueNumber !== undefined && answer.valueNumber !== "") return true;
    return String(answer.valueText || "").trim() !== "";
}

// Headers break after "/" ("PP/36/1") instead of in the middle of a word.
function headerHtml(label) {
    return escapeHtml(label).replace(/\//g, "/<wbr>");
}

function tableHtml(answer) {
    const data = tableAnswerData(answer);
    if (!data) return `<p>${multiline(answer.valueText)}</p>`;
    // Fixed rows need their labels; a dynamic table only has the summary
    // label, which goes in the first column so no width is wasted.
    const fixed = (data.rows || {}).mode === "fixed";
    const withLabels = fixed && tableHasRowLabels(data);
    const wide = data.columns.length > 8 ? "wide" : "";
    const head = (withLabels ? "<th></th>" : "") +
        data.columns.map((col) => `<th class="${col.type === "number" ? "num" : ""}">${headerHtml(col.label)}</th>`).join("");
    const body = tableDisplayRows(data).map((row) => {
        const label = withLabels ? `<th class="row-label">${escapeHtml(row.label)}</th>` : "";
        const cells = row.cells.map((cell, j) => {
            const num = data.columns[j].type === "number" ? "num" : "";
            const calc = cell.formula || row.kind === "summary" ? "calc" : "";
            let text = cell.text;
            // Same as the form: a zero total is left blank.
            if (row.kind === "summary" && cell.raw === 0) text = "";
            if (!withLabels && j === 0 && row.kind === "summary" && !text) text = row.label;
            return `<td class="${num} ${calc}">${escapeHtml(text)}</td>`;
        }).join("");
        return `<tr class="${row.kind === "summary" ? "summary" : ""}">${label}${cells}</tr>`;
    }).join("");
    return `<table class="grid ${wide}"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function filesHtml(answer) {
    const files = (answer.files || [])
        .map((file) => ({ url: safeUrl(file.url), name: file.name || "" }))
        .filter((file) => file.url);
    if (!files.length) return "";
    const images = files.filter((file) => IMAGE_EXTENSION.test(file.name) || IMAGE_EXTENSION.test(file.url));
    const others = files.filter((file) => images.indexOf(file) === -1);
    const imgs = images.map((file) =>
        `<figure><img src="${escapeHtml(file.url)}" alt="${escapeHtml(file.name)}"><figcaption>${escapeHtml(file.name)}</figcaption></figure>`,
    ).join("");
    const links = others.map((file) =>
        `<li><a href="${escapeHtml(file.url)}">${escapeHtml(file.name || file.url)}</a></li>`,
    ).join("");
    return (imgs ? `<div class="images">${imgs}</div>` : "") + (links ? `<ul class="files">${links}</ul>` : "");
}

// valueNumber comes as a decimal string ("9.000").
function score(value) {
    const n = parseFloat(value);
    return isNaN(n) ? String(value) : String(n).replace(".", ",");
}

const SHORT_TYPES = ["input", "date", "selectbox", "boolean", "nps", "ces", "emotion"];

// One-line answers share a row (three per line) so the sheet stays short.
function isShort(answer) {
    if (SHORT_TYPES.indexOf(answer.type) === -1) return false;
    const text = String(answer.valueText || "");
    return text.length <= 60 && text.indexOf("\n") === -1 && String(answer.question || "").length <= 60;
}

function valueHtml(answer, t) {
    if (!isAnswered(answer)) return `<p class="muted">${escapeHtml(t("inbox-message-unanswered"))}</p>`;
    switch (answer.type) {
        case "boolean":
            return `<p>${escapeHtml(parseInt(answer.valueNumber, 10) > 0 ? t("inbox-message-bool_yes") : t("inbox-message-bool_no"))}</p>`;
        case "emotion":
            return `<p>${escapeHtml(score(answer.valueNumber))} / 5</p>`;
        case "nps":
            return `<p>${escapeHtml(score(answer.valueNumber))} / 10</p>`;
        case "ces":
            return `<p>${escapeHtml(score(answer.valueNumber))} / 7</p>`;
        case "table":
            return tableHtml(answer);
        case "file":
            return filesHtml(answer);
        default:
            return `<p>${multiline(answer.valueText)}</p>`;
    }
}

const STYLES = `
@page { size: A4; margin: 16mm 14mm; }
* { box-sizing: border-box; }
body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111827; font-size: 12px; line-height: 1.45; margin: 0; padding: 24px; }
header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 16px; }
header img { max-height: 48px; max-width: 160px; }
h1 { font-size: 18px; margin: 0; }
.meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px 16px; margin: 0 0 18px; }
.meta div span { display: block; color: #6b7280; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
.answers { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); column-gap: 16px; }
.question { grid-column: 1 / -1; padding: 8px 0; border-bottom: 1px solid #e5e7eb; break-inside: avoid; page-break-inside: avoid; }
.question.short { grid-column: auto; }
.question h2 { font-size: 12px; font-weight: 700; margin: 0 0 4px; }
.question p { margin: 0; white-space: normal; }
.muted { color: #9ca3af; }
table.grid { border-collapse: collapse; width: 100%; font-size: 10.5px; margin-top: 4px; }
table.grid th, table.grid td { border: 1px solid #d1d5db; padding: 4px 6px; text-align: left; vertical-align: top; }
table.grid td { overflow-wrap: anywhere; }
table.grid th { overflow-wrap: normal; word-break: normal; }
table.grid.wide { font-size: 8.5px; }
table.grid.wide th, table.grid.wide td { padding: 3px 4px; }
table.grid thead th { background: #f3f4f6; font-weight: 600; }
table.grid .num { text-align: right; font-variant-numeric: tabular-nums; }
table.grid .calc { font-weight: 600; }
table.grid tr.summary td, table.grid tr.summary th { background: #f9fafb; font-weight: 700; }
table.grid .row-label { background: #fafafa; white-space: nowrap; }
.images { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 6px; }
.images figure { margin: 0; width: 200px; }
.images img { max-width: 200px; max-height: 200px; border: 1px solid #e5e7eb; }
.images figcaption { font-size: 9px; color: #6b7280; word-break: break-all; }
.files { margin: 4px 0 0; padding-left: 18px; }
footer { margin-top: 18px; color: #9ca3af; font-size: 9px; }
@media print { body { padding: 0; } a { color: inherit; } }
`;

/*
  message: the inbox message (form_name, created, client fields, answers).
  answers: the questions to print, already filtered by visibility.
  t: the admin translate function. place: {name, logo}.
*/
export function buildAnswerPrintHtml({ message, answers, t, place, formatDate }) {
    const client = [message.first_name, message.last_name].filter(Boolean).join(" ");
    const meta = [
        [t("inbox-message-print_client"), client],
        [t("inbox-message-print_email"), message.email],
        [t("inbox-message-print_phone"), message.whatsapp],
        [t("inbox-message-print_answered_at"), formatDate ? formatDate(message.created) : message.created],
        [t("inbox-message-print_answer_id"), message.id],
    ].filter(([, value]) => value);
    const logo = place && safeUrl(place.logo);
    const questions = (answers || [])
        // Only what was answered goes on paper.
        .filter((answer) => answer.type !== "content" && isAnswered(answer))
        .map((answer) => `<section class="question${isShort(answer) ? " short" : ""}"><h2>${escapeHtml(answer.question)}</h2>${valueHtml(answer, t)}</section>`)
        .join("");
    return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(message.form_name)} — ${escapeHtml(client || message.id)}</title><style>${STYLES}</style></head><body>
<header>${logo ? `<img src="${escapeHtml(logo)}" alt="">` : ""}<div><h1>${escapeHtml(message.form_name)}</h1>${place && place.name ? `<div class="muted">${escapeHtml(place.name)}</div>` : ""}</div></header>
<div class="meta">${meta.map(([label, value]) => `<div><span>${escapeHtml(label)}</span>${escapeHtml(value)}</div>`).join("")}</div>
<main class="answers">${questions}</main>
<footer>${escapeHtml(t("inbox-message-print_footer"))}</footer>
</body></html>`;
}

/*
  Opens the print window. Must run from the click handler, or the popup
  blocker stops it. Waits for images (logo, attached art) before printing so
  they end up in the PDF.
*/
export function printAnswer(options) {
    const win = window.open("", "_blank");
    if (!win) return false;
    win.document.open();
    win.document.write(buildAnswerPrintHtml(options));
    win.document.close();
    const images = Array.from(win.document.images || []);
    const loaded = images.map((img) =>
        img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
            }),
    );
    const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
    Promise.race([Promise.all(loaded), timeout]).then(() => {
        win.focus();
        win.print();
    });
    return true;
}
